'use strict'

import React, { useRef, useState, useEffect } from 'react'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CustomTooltip from '../../../../_components/CustomTooltip'
import { useConversionTableSelector } from '../extra/store'
import { getTableName } from '../extra/helpers'

/** 区分マスター設定（ユーザー設定）の DataBridgeImport / DataBridgeExport と同一のボタン見た目 */
const importButtonClass = (disabled) =>
  `rounded-lg bg-white border-[#383947] border h-[45px] items-center text-sm text-[#383947b3] px-4 py-2 inline-flex gap-2 ` +
  `${!disabled && 'hover:text-[#383947] hover:font-medium hover:border-1.5 hover:border-[#383947]'} ` +
  `${disabled && 'bg-gray-300/40 cursor-not-allowed'}`

const exportButtonClass = (disabled) =>
  `rounded-lg bg-white border-[#383947] border h-[45px] items-center text-sm text-[#383947b3] px-4 py-2 inline-flex gap-2 ` +
  `${!disabled && 'hover:text-[#383947] hover:font-medium hover:border-1.5 hover:border-[#383947]'} ` +
  `${disabled && 'bg-gray-300/40 cursor-not-allowed'}`

/**
 * 変換表ツールバーコンポーネント
 *
 * @param {Object} props
 * @param {function(string): void} props.onSheetChange - シート変更ハンドラー
 * @param {function(File, string): void} props.onImportFile - ファイルインポートハンドラー
 * @param {function('csv' | 'xlsx'): void} props.onExport - エクスポートハンドラー
 * @returns {JSX.Element}
 */
const ConversionTableToolbar = ({ onSheetChange, onImportFile, onExport }) => {
  const state = useConversionTableSelector((s) => s)
  const selectedTable = state.selectedTable
  const previewItems = state.previewItems
  const conversionItems = state.conversionItems
  const importFileName = state.importFileName
  const importSheetNames = state.importSheetNames
  const selectedSheetName = state.selectedSheetName

  const isPreviewMode = previewItems !== null
  const displayItems = isPreviewMode ? previewItems : conversionItems
  const hasItems = displayItems && displayItems.length > 0
  const disabled = !selectedTable
  const tableName = selectedTable ? getTableName(selectedTable) : '変換表を選択してください'

  const fileInputRef = useRef(null)
  const dropdownRef = useRef(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) onImportFile(file, selectedSheetName)
    e.target.value = ''
  }

  const toggleDropdown = () => {
    if (!hasItems) return
    setIsDropdownOpen((prev) => !prev)
  }

  const handleExport = (format) => {
    onExport(format)
    setIsDropdownOpen(false)
  }

  useEffect(() => {
    if (!isDropdownOpen) return

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  return (
    <div className="toolbar flex flex-wrap items-center gap-x-2 gap-y-2 border-b border-[#d8dee9] p-3 min-w-0">
      <div className="min-w-0 max-w-md overflow-hidden">
        <CustomTooltip title={tableName} placement="top">
          {(ref) => (
            <div ref={ref} className="block w-full min-w-0 truncate font-semibold text-[#383947]">
              {tableName}
            </div>
          )}
        </CustomTooltip>
      </div>
      {isPreviewMode && (
        <span
          className="preview-badge ml-2 rounded-full bg-[#fff8e6] px-2 py-0.5 text-xs font-bold text-[#b45309]"
          data-testid="preview-badge"
        >
          プレビュー
        </span>
      )}
      {importFileName && (
        <div className="min-w-0 max-w-md shrink overflow-hidden">
          <CustomTooltip title={importFileName} placement="top">
            {(ref) => (
              <div
                ref={ref}
                data-testid="import-file-name"
                className="block w-full min-w-0 truncate text-sm text-[#383947]"
              >
                {importFileName}
              </div>
            )}
          </CustomTooltip>
        </div>
      )}
      {importSheetNames.length > 0 && (
        <select
          className="input rounded-lg border border-gray-300 px-2 py-2 text-sm"
          value={selectedSheetName}
          onChange={(e) => onSheetChange(e.target.value)}
        >
          {importSheetNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      )}
      <div className="min-w-[8px] flex-1" aria-hidden="true" />
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={handleFileChange}
        data-testid="file-import-input"
        className="hidden"
      />
      <div className="footer_button_area !m-0 inline-flex !w-fit !justify-center !gap-x-2">
        <button
          type="button"
          id="importBtn"
          disabled={disabled}
          onClick={handleImportClick}
          className={importButtonClass(disabled)}
        >
          <CloudUploadIcon fontSize="small" />
          インポート
        </button>
        <div ref={dropdownRef} className="relative">
          <button
            type="button"
            id="exportBtn"
            disabled={!hasItems}
            onClick={toggleDropdown}
            data-testid="export-button"
            className={exportButtonClass(!hasItems)}
          >
            <CloudDownloadIcon fontSize="small" />
            エクスポート
            <ArrowDropDownIcon fontSize="small" />
          </button>
          {isDropdownOpen && (
            <div className="absolute right-0 z-[100] mt-1 w-48 rounded-lg border border-gray-300 bg-white shadow-lg">
              <button
                type="button"
                onClick={() => handleExport('csv')}
                data-testid="export-csv-option"
                className="w-full rounded-t-lg px-4 py-2 text-left text-sm text-[#383947b3] first:rounded-t-lg hover:bg-gray-100"
              >
                CSV形式
              </button>
              <button
                type="button"
                onClick={() => handleExport('excel')}
                data-testid="export-excel-option"
                className="w-full rounded-b-lg px-4 py-2 text-left text-sm text-[#383947b3] last:rounded-b-lg hover:bg-gray-100"
              >
                Excel形式
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ConversionTableToolbar
